import { useEffect, useState } from "react";
import { CalendarCheck2, Check, Pencil, X } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fmtDateTime } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { FibiLoading } from "../components/FibiLoading";
import { DatePicker } from "../components/DatePicker";

export default function SetupAgen() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [agen, setAgen] = useState("");
  useEffect(() => { setData(null); setAgen(""); api.get("/reports/setup-agen", { params: { month } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))); }, [month]);
  const [edit, setEdit] = useState(null); // { key, date, saving }
  const simpan = async (r) => {
    if (!edit?.date) return;
    setEdit({ ...edit, saving: true });
    try {
      await api.put(`/routers/${r.router_id}/ppp/${encodeURIComponent(r.name)}/setup`, { expired_date: edit.date, status: "keep" });
      toast.success(`Jatuh tempo ${r.name} dikoreksi ke ${fmtDate(edit.date)}`);
      setData({ ...data, rows: data.rows.map((x) => (x.router_id === r.router_id && x.name === r.name ? { ...x, expired_date: edit.date, corrected_by_admin: true } : x)) });
      setEdit(null);
    } catch (e) { toast.error(errMsg(e)); setEdit({ ...edit, saving: false }); }
  };
  const rows = (data?.rows || []).filter((r) => !agen || r.email === agen);
  const agens = Object.entries(data?.per_agen || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-4" data-testid="setup-agen-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold flex items-center gap-2"><CalendarCheck2 className="w-6 h-6 text-sky-500" /> Laporan Setup Agen</h1>
          <p className="text-xs text-muted-foreground">Jatuh tempo WiFi Rumahan yang di-set <b>agen</b> (migrasi sistem lama, maks 30 Okt 2026). Periksa tanggalnya; klik nama pelanggan untuk membuka secret-nya.</p>
        </div>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 w-44" data-testid="setup-agen-month" />
      </div>
      {data && (
        <div className="flex flex-wrap gap-1.5 text-xs" data-testid="setup-agen-summary">
          <button type="button" onClick={() => setAgen("")} className={`px-2 py-1 rounded-full border ${!agen ? "border-sky-500 text-sky-400" : "border-border text-muted-foreground"}`} data-testid="setup-agen-filter-all">Semua ({data.total})</button>
          {agens.map(([em, n]) => <button key={em} type="button" onClick={() => setAgen(em === agen ? "" : em)} className={`px-2 py-1 rounded-full border font-mono ${agen === em ? "border-sky-500 text-sky-400" : "border-border text-muted-foreground"}`} data-testid={`setup-agen-filter-${em}`}>{em} ({n})</button>)}
        </div>
      )}
      <div className="rounded-xl border border-border bg-card overflow-x-auto">
        <Table className="text-xs">
          <TableHeader><TableRow><TableHead>#</TableHead><TableHead>Waktu set</TableHead><TableHead>Agen</TableHead><TableHead>Cabang</TableHead><TableHead>Pelanggan</TableHead><TableHead>Jatuh tempo di-set</TableHead></TableRow></TableHeader>
          <TableBody>
            {!data ? <TableRow><TableCell colSpan={6} className="p-0"><FibiLoading text="FIBI sedang memuat data..." size={20} /></TableCell></TableRow>
              : !rows.length ? <TableRow><TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="setup-agen-empty">Belum ada jatuh tempo yang di-set agen pada bulan ini</TableCell></TableRow>
              : rows.map((r, i) => (
                <TableRow key={`${r.router_id}-${r.name}`} data-testid={`setup-agen-row-${i}`}>
                  <TableCell>{i + 1}</TableCell>
                  <TableCell className="font-mono whitespace-nowrap">{fmtDateTime(r.set_at)}</TableCell>
                  <TableCell>{r.agent_code && `[${r.agent_code}] `}{r.agent_name || r.email}<div className="text-[10px] text-muted-foreground font-mono">{r.email}</div></TableCell>
                  <TableCell>{r.router}</TableCell>
                  <TableCell><Link to={`/ppp?router=${r.router_id}&secret=${encodeURIComponent(r.name)}`} className="font-semibold text-sky-400 hover:underline" data-testid={`setup-agen-link-${r.name}`}>{r.name}</Link></TableCell>
                  <TableCell className={`font-mono font-bold ${r.expired_date > "2026-10-30" ? "text-rose-400" : "text-emerald-400"}`}>
                    {edit?.key === `${r.router_id}-${r.name}` ? (
                      <span className="inline-flex items-center gap-1">
                        <DatePicker value={edit.date} onChange={(v) => setEdit({ ...edit, date: v })} className="h-7 w-36 text-xs" testid={`setup-agen-edit-date-${r.name}`} />
                        <Button size="icon" variant="ghost" className="h-7 w-7 text-emerald-500" disabled={edit.saving || !edit.date} onClick={() => simpan(r)} data-testid={`setup-agen-save-${r.name}`}><Check className="w-4 h-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-7 w-7" disabled={edit.saving} onClick={() => setEdit(null)} data-testid={`setup-agen-cancel-${r.name}`}><X className="w-4 h-4" /></Button>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5">
                        {fmtDate(r.expired_date)}
                        {r.corrected_by_admin && <span className="px-1.5 py-0.5 rounded text-[10px] font-normal bg-sky-500/15 text-sky-400 border border-sky-500/30">dikoreksi admin</span>}
                        <Button size="icon" variant="ghost" className="h-6 w-6 text-sky-400" title="Koreksi tanggal jatuh tempo" onClick={() => setEdit({ key: `${r.router_id}-${r.name}`, date: r.expired_date, saving: false })} data-testid={`setup-agen-edit-${r.name}`}><Pencil className="w-3.5 h-3.5" /></Button>
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
